<template>
    <nav
        aria-label="Пагинация"
        class="flex items-center space-x-1 md:space-x-4">
        <NuxtLink
            :to="
                canGoFirstOrPrev && pagination.current_page !== 1
                    ? `${urlWithoutPage}${PAGE_KEY}=${pagination.current_page - 1}`
                    : undefined
            "
            :rel="canGoFirstOrPrev ? 'prev' : undefined"
            :aria-current="undefined"
            :class="
                canGoFirstOrPrev
                    ? 'text-accent-100 hover:text-accent-300 active:opacity-70'
                    : 'pointer-events-none text-ash-200/20'
            "
            class="flex items-center justify-center rounded-xl p-1 transition-all duration-300 md:p-2"
            @click.prevent="onClickPage">
            <AtomIcon
                name="chevron-left"
                class="size-7" />
        </NuxtLink>
        <ul class="flex space-x-1 text-16/150 font-semibold md:space-x-2">
            <li
                v-for="(page, index) of displayedPages"
                :key="`${page}-${index}`">
                <NuxtLink
                    v-if="typeof page === 'number'"
                    :to="getUrlForPagination(page)"
                    :aria-current="
                        pagination.current_page === page ? true : undefined
                    "
                    :aria-label="`Страница ${page}`"
                    :class="
                        Number(pagination.current_page) === page
                            ? 'bg-gradient-button'
                            : 'bg-white/20 text-white'
                    "
                    class="transition-color flex size-12 items-center justify-center rounded-8 duration-300 hover:bg-[#B591FC] active:bg-solid-video-title"
                    @click.prevent="onClickPage">
                    {{ page }}
                </NuxtLink>
                <span
                    v-else
                    class="pointer-events-none flex items-center justify-center px-4 py-2">
                    {{ page }}
                </span>
            </li>
        </ul>
        <NuxtLink
            :to="
                canGoLastOrNext
                    ? `${urlWithoutPage}${PAGE_KEY}=${pagination.current_page + 1}`
                    : undefined
            "
            :rel="canGoLastOrNext ? 'next' : undefined"
            :aria-current="undefined"
            :class="
                canGoLastOrNext
                    ? 'text-accent-100 hover:text-accent-300 active:opacity-70'
                    : 'pointer-events-none text-ash-200/20'
            "
            class="flex items-center justify-center rounded-xl p-1 transition-all duration-300 md:p-2"
            @click.prevent="onClickPage">
            <AtomIcon
                name="chevron-right"
                class="size-7" />
        </NuxtLink>
    </nav>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'

import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'

interface BasePaginationTypes {
    total: number
    count: number
    per_page: number
    current_page: number
    total_pages: number
    links: {
        previous?: string
        next?: string
    }
    available_per_pages: number[]
}

const PAGE_KEY = 'page'

const props = defineProps({
    /**
     * Объект приходящий из запроса
     */
    pagination: {
        type: Object as PropType<BasePaginationTypes>,
        required: true,
    },
    /**
     * Максимальное количество отображаемых кнопок (минимальное кол-во 5)
     */
    max: {
        type: Number as PropType<number>,
        default: 5,
    },
    /**
     * Разделитель, который выводится если кнопок больше props.max
     */
    divider: {
        type: String as PropType<string>,
        default: '...',
    },
    topOnScroll: {
        type: Number as PropType<number>,
        default: 300,
    },
})

const displayedPages = computed(() => {
    const maxDisplayedPages = Math.max(props.max, 5)
    const r = Math.floor(
        (Math.min(maxDisplayedPages, props.pagination?.total_pages) - 5) / 2
    )
    const r1 = props.pagination.current_page - r
    const r2 = props.pagination.current_page + r

    const beforeWrapped = r1 - 1 > 1
    const afterWrapped = r2 + 1 < props.pagination?.total_pages

    const items: Array<number | string> = []
    if (props.pagination?.total_pages <= maxDisplayedPages) {
        for (let i = 1; i <= props.pagination?.total_pages; i++) {
            items.push(i)
        }
        return items
    }

    items.push(1)

    if (beforeWrapped) items.push(props.divider)

    if (!afterWrapped) {
        const addedItems =
            props.pagination.current_page +
            r +
            2 -
            props.pagination?.total_pages
        for (
            let i = props.pagination.current_page - r - addedItems;
            i <= props.pagination.current_page - r - 1;
            i++
        ) {
            items.push(i)
        }
    }

    for (
        let i = Math.max(2, r1);
        i <= Math.min(props.pagination?.total_pages, r2);
        i++
    ) {
        items.push(i)
    }

    if (!beforeWrapped) {
        const addedItems = 1 - (props.pagination.current_page - r - 2)
        for (
            let i = props.pagination.current_page + r + 1;
            i <= props.pagination.current_page + r + addedItems;
            i++
        ) {
            items.push(i)
        }
    }

    if (afterWrapped) items.push(props.divider)

    if (r2 < props.pagination?.total_pages) {
        items.push(props.pagination?.total_pages)
    }

    // Replace divider by number on start edge case [1, '…', 3, ...]
    if (items.length >= 3 && items[1] === props.divider && items[2] === 3) {
        items[1] = 2
    }

    // Replace divider by number on end edge case [..., 48, '…', 50]
    if (
        items.length >= 3 &&
        items[items.length - 2] === props.divider &&
        items[items.length - 1] === items.length
    ) {
        items[items.length - 2] = items.length - 1
    }

    return items
})

const canGoFirstOrPrev = computed(() => props.pagination.current_page > 1)
const canGoLastOrNext = computed(
    () => props.pagination.current_page < props.pagination?.total_pages
)

const route = useRoute()
const urlWithoutPage = computed(() => {
    let url = route.path + '?'

    url += Object.entries(route.query)
        .filter((entry) => entry[0] !== PAGE_KEY)
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                const q = []
                for (let i = 0; i < value.length; i++) {
                    q.push(`${key}=${value[i]}`)
                }
                return q.join('&')
            }
            return `${key}=${value}`
        })
        .join('&')
    if (!url.endsWith('&') && !url.endsWith('?')) {
        url += '&'
    }

    return url
})

function getUrlForPagination(page: number) {
    if (props.pagination.current_page === page) return undefined

    let url = urlWithoutPage.value
    if (page === 1) {
        if (url.endsWith('?')) {
            url = url.replace('?', '')
        }

        return url
    }

    return url + PAGE_KEY + '=' + page
}

function onClickPage() {
    setTimeout(() => {
        window.scrollTo({
            top: props.topOnScroll,
            behavior: 'smooth',
        })
    }, 500)
}
</script>
